import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { touchTargetSx } from "_shared/_views/touch-target";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import { useGetPermissionTargetsByIdsQuery } from "permissions/_api/permission-targets-api-service";
import {
  ForumAccessRule,
  type RecordAccessAnswer,
  type RecordAccessReason,
  type RecordAccessRemedy,
  RecordAccessRemedyRule,
  RecordAccessRule,
  RecordAccessWithholding,
  RecordAction,
  type RecordActionAnswer,
  RecordActionRefusal,
  recordActionsOf,
  useCheckRecordAccessQuery,
  useCheckRecordActionQuery,
} from "permissions/_api/record-access-api-service";
import { PersonPicker } from "permissions/_views/records/people-picker";
import { type CheckedRecordDomain, recordPermissionsPath } from "permissions/_views/records/record-permissions-path";
import type { PermissionGranteeOption } from "permissions/_views/search/use-permission-grantee-typeahead";
import {
  FIELD_NAMES,
  forumAccessReasons,
  grantDate,
  levelLabel,
  recordAccessReasons,
  recordAccessRemedies,
  recordActions,
  recordPage,
} from "permissions/_views/wording/permission-wording";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import type { WorkAgreementField } from "permissions/resources/work/work-agreement-field";
import { type ReactNode, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";

/**
 * Check access on a record page: "can this person open it?", with the effective answer and every
 * grant or roster rule that decides it (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "The
 * record page").
 *
 * The answer is the server's (#6190), read through the query the record's own reads assert, so it
 * cannot drift from what the record lets in. A "No" says what would turn it into "Yes", from the
 * server's remedies (#6833), and offers the ones the server says this viewer may make: a pre-filled
 * "Give access", or the page of the proposal whose field keeps the person out.
 *
 * A record type with actions beyond Open offers a choice of them (#6830): Edit, and on a proposal
 * Approve (#7153). An action's answer is the server's too, from that action's own check, with the rule
 * that refuses it.
 */

type GrantorName = (userId: string) => string | undefined;

/** One reason's line on a conversation, in the wording module's words. */
function forumReasonText(reason: RecordAccessReason, grantorName: GrantorName): string {
  switch (reason.rule) {
    case ForumAccessRule.Direct:
      return forumAccessReasons.direct(reason.grantorUserId ? grantorName(reason.grantorUserId) : undefined);
    case ForumAccessRule.Parent:
      return forumAccessReasons.parent;
    case ForumAccessRule.Roster:
      return forumAccessReasons.roster(reason.source ?? undefined);
    case ForumAccessRule.LeftRoster:
      return forumAccessReasons.leftRoster;
    case ForumAccessRule.System:
      return forumAccessReasons.system;
    case ForumAccessRule.Creator:
      return forumAccessReasons.creator;
    default:
      return forumAccessReasons.other;
  }
}

/** One reason's line on any record but a conversation, in the wording module's words. */
function recordReasonText(reason: RecordAccessReason, noun: string, grantorName: GrantorName): string {
  switch (reason.rule) {
    case RecordAccessRule.Direct:
      return recordAccessReasons.direct(reason.grantorUserId ? grantorName(reason.grantorUserId) : undefined);
    case RecordAccessRule.System:
      return recordAccessReasons.system;
    case RecordAccessRule.Parent:
      return recordAccessReasons.parent;
    case RecordAccessRule.DerivedProposal:
      return recordAccessReasons.derivedProposal(noun);
    case RecordAccessRule.Roster:
      return recordAccessReasons.roster(noun);
    case RecordAccessRule.EntityWide:
      return recordAccessReasons.entityWide(noun);
    case RecordAccessRule.LeftRoster:
      return recordAccessReasons.leftRoster;
    case RecordAccessRule.OtherOrganization:
      return recordAccessReasons.otherOrganization;
    default:
      return recordAccessReasons.other;
  }
}

/** One reason's line, in the wording module's words: a conversation's rules are Messaging's, the rest the record's. */
export function reasonText(domain: CheckedRecordDomain, reason: RecordAccessReason, noun: string, grantorName: GrantorName): string {
  const text = domain === "forums" ? forumReasonText(reason, grantorName) : recordReasonText(reason, noun, grantorName);
  // Only a conversation's answer carries each grant's level.
  return reason.accessLevel === undefined ? text : `${levelLabel({ accessLevel: reason.accessLevel as AccessLevel })} · ${text}`;
}

/** A standing kind's name for a sentence, from the kinds endpoint, or its slug in words until that loads. */
function kindName(kinds: readonly PermissionKind[] | undefined, slug: string): string {
  return kinds?.find((kind) => kind.slug === slug)?.name ?? slug.replaceAll("-", " ");
}

/** A field's name as the field editor shows it, or the server's name for a field this page does not know. */
function fieldName(field: string): string {
  return FIELD_NAMES[field as WorkAgreementField] ?? field;
}

/**
 * What would open the record when nothing reaches it: a grant on it, a standing kind, the roster.
 * Null when the server sent no such remedy, as a server from before #6833 does.
 */
export function nothingReachesText(remedies: readonly RecordAccessRemedy[], noun: string, kinds?: readonly PermissionKind[]): string | null {
  if (!remedies.some((remedy) => remedy.rule === RecordAccessRemedyRule.RecordGrant)) return null;
  const standing = remedies.find((remedy) => remedy.rule === RecordAccessRemedyRule.StandingGrant)?.permissionKind;
  const roster = remedies.some((remedy) => remedy.rule === RecordAccessRemedyRule.Roster);
  const alsoField = remedies.some((remedy) => remedy.rule === RecordAccessRemedyRule.ProposalField);
  return recordAccessRemedies.nothingReaches(noun, standing ? kindName(kinds, standing) : undefined, roster, alsoField);
}

/**
 * The answer's first line: whether the person can open it, and at what level when the server says.
 * Only a conversation's answer has a level; the others answer "can open it" alone.
 */
export function answerText(check: RecordAccessAnswer, person: string, noun: string): string {
  if (!check.canOpen) return recordPage.cannotOpen(person, noun);
  if (check.accessLevel === undefined) return recordPage.canOpenUnlevelled(person);
  return recordPage.canOpen(person, levelLabel({ accessLevel: check.accessLevel as AccessLevel }));
}

/** The withheld line under reasons that reach the record and still do not open it. */
function withheldText(withheldBy: string): string {
  switch (withheldBy) {
    case RecordAccessWithholding.ProposalField:
      return recordAccessReasons.withheldByProposalField;
    case RecordAccessWithholding.UnsubmittedDraft:
      return recordAccessReasons.withheldByUnsubmittedDraft;
    default:
      return recordPage.withheld;
  }
}

/** Whose approval a proposal waits for instead, as far as the viewer may see: named colleagues, and whether another organization's too. */
export type AwaitedApproval = Readonly<{ names: readonly string[]; otherOrganization: boolean }>;

/** Why an action is refused on a record the person opens, in the wording module's words. */
export function refusalText(refusedBy: string, noun: string, awaited?: AwaitedApproval, action?: RecordAction): string {
  switch (refusedBy) {
    case RecordActionRefusal.ReadOnly:
      return recordActions.refusals.readOnly;
    case RecordActionRefusal.WorkEnded:
      return recordActions.refusals.workEnded(noun);
    case RecordActionRefusal.OutsideAudience:
      return action === RecordAction.Invite ? recordActions.refusals.outsideAudienceToInvite : recordActions.refusals.outsideAudience;
    case RecordActionRefusal.NotOnProposal:
      return recordActions.refusals.notOnProposal;
    case RecordActionRefusal.RepresentsBothParties:
      return recordActions.refusals.representsBothParties;
    case RecordActionRefusal.NotEditingReviewer:
      return recordActions.refusals.notEditingReviewer;
    case RecordActionRefusal.TeamMemberOnly:
      return recordActions.refusals.teamMemberOnly;
    case RecordActionRefusal.ProposalClosed:
      return recordActions.refusals.proposalClosed;
    case RecordActionRefusal.NoMemberInvitations:
      return recordActions.refusals.noMemberInvitations;
    case RecordActionRefusal.NotConversationCreator:
      return recordActions.refusals.notConversationCreator;
    case RecordActionRefusal.NotLeader:
      return recordActions.refusals.notLeader;
    case RecordActionRefusal.NotAwaitingTheirApproval:
      return recordActions.refusals.notAwaitingTheirApproval(awaited?.names ?? [], awaited?.otherOrganization ?? false);
    case RecordActionRefusal.NotAssignedReviewer:
      return recordActions.refusals.notAssignedReviewer;
    case RecordActionRefusal.NoSigningAuthority:
      return recordActions.refusals.noSigningAuthority;
    case RecordActionRefusal.SigningAuthorityTooNarrow:
      return recordActions.refusals.signingAuthorityTooNarrow;
    case RecordActionRefusal.NotClientLeader:
      return recordActions.refusals.notClientLeader;
    case RecordActionRefusal.WorkAlreadyAwarded:
      return recordActions.refusals.workAlreadyAwarded;
    case RecordActionRefusal.NotPrincipal:
      return recordActions.refusals.notPrincipal;
    case RecordActionRefusal.AgreementNotOpen:
      return recordActions.refusals.agreementNotOpen;
    case RecordActionRefusal.AgreementIncomplete:
      return recordActions.refusals.agreementIncomplete;
    default:
      return recordActions.refusals.other;
  }
}

/** The fields an allowed edit leaves out, by their names on the page; a field the page does not know keeps the server's name. */
function fieldNames(fields: readonly string[]): string[] {
  return fields.map(fieldName);
}

/** The names of the people who granted an action's reasons, and of those its approval waits for, as far as they have loaded. */
function useActionPeopleNames(check: RecordActionAnswer): GrantorName {
  const userIds = useMemo(
    () =>
      new Set(
        [...check.reasons.flatMap((reason) => (reason.grantorUserId ? [reason.grantorUserId] : [])), ...check.awaitedApprovers].filter((id) =>
          Guid.isValid(id),
        ),
      ),
    [check.reasons, check.awaitedApprovers],
  );
  const grantors = useGetIndividualsByUserIdsQuery(userIds.size > 0 ? { userIds: [...userIds] } : skipToken);
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const individual of Individual.fromJSONArray(grantors.data, { allowEmpty: true }) ?? []) {
      if (individual.userId && individual.fullName) map.set(individual.userId.value.toLowerCase(), individual.fullName);
    }
    return map;
  }, [grantors.data]);
  return (id) => names.get(id.toLowerCase());
}

function ActionAnswer(
  props: Readonly<{
    domain: CheckedRecordDomain;
    check: RecordActionAnswer;
    person: PermissionGranteeOption;
    noun: string;
    verb: string;
    onGiveAccess?: (person: PermissionGranteeOption, accessLevel?: AccessLevel) => void;
  }>,
) {
  const { check, person, noun } = props;
  const personName = useActionPeopleNames(check);
  const awaited: AwaitedApproval = {
    names: check.awaitedApprovers.map((id) => personName(id) ?? recordActions.unnamedColleague),
    otherOrganization: check.awaitsOtherOrganization,
  };
  const Icon = check.allowed ? CheckCircleOutlineIcon : DoNotDisturbOnOutlinedIcon;
  const refusedButOpens = !check.allowed && check.canOpen && check.reasons.length > 0;
  const isSign = check.action === RecordAction.Sign;
  // An allowed hire or lead names the signing authority that allows it; an organization agreement's
  // acceptance asks no signing authority, so its grants are the agreement's own.
  const signsThroughAuthority = isSign && check.allowed && props.domain !== "organization-agreements";
  const signingAuthorityRefused =
    check.refusedBy === RecordActionRefusal.NoSigningAuthority || check.refusedBy === RecordActionRefusal.SigningAuthorityTooNarrow;
  // A grant answers only a refusal a grant can lift: nothing reaching the record, or reaching it at
  // Can read. A seat on a proposal, or the end of the work, is not given on this page, and the server
  // names that refusal even when nothing reaches the record.
  const needsCanEdit = check.action === RecordAction.Edit || (isSign && props.domain === "organization-agreements");
  const grantWouldHelp =
    !check.allowed && ((!check.canOpen && !check.withheldBy && !check.refusedBy) || check.refusedBy === RecordActionRefusal.ReadOnly);

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Icon color={check.allowed ? "success" : "disabled"} sx={{ mt: 0.25 }} aria-hidden />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {check.allowed ? recordActions.allowed(person.label, props.verb) : recordActions.refused(person.label, props.verb, noun)}
        </Typography>
        {check.allowed && check.restrictedFields.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {recordActions.exceptFields(fieldNames(check.restrictedFields))}
          </Typography>
        )}
        {check.refusedBy && (
          <Typography variant="body2" color="text.secondary">
            {refusalText(check.refusedBy, noun, awaited, check.action as RecordAction)}
          </Typography>
        )}
        {signingAuthorityRefused && (
          <Typography variant="body2" color="text.secondary">
            {recordActions.signingAuthorityWouldLetThem}
          </Typography>
        )}
        {!check.allowed && !check.canOpen && !check.withheldBy && (
          <Typography variant="body2" color="text.secondary">
            {recordPage.cannotOpen(person.label, noun)}
          </Typography>
        )}
        {refusedButOpens && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {recordActions.whatTheyHold}
          </Typography>
        )}
        {signsThroughAuthority && check.reasons.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {recordActions.theirSigningAuthority}
          </Typography>
        )}
        {check.reasons.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {check.reasons.map((reason) => (
              <Typography component="li" variant="body2" color="text.secondary" key={reason.key}>
                {reasonText(props.domain, reason, noun, personName)}
              </Typography>
            ))}
          </Box>
        )}
        {check.withheldBy && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {withheldText(check.withheldBy)}
          </Typography>
        )}
        {isSign && props.domain === "organization-agreements" && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {recordActions.acceptanceNeedsNoSigningAuthority}
          </Typography>
        )}
        {grantWouldHelp && props.onGiveAccess && (
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
            // An edit and an organization agreement's acceptance need Can edit, so Add people opens there;
            // approving, hiring and leading need only to open the record.
            onClick={() => props.onGiveAccess?.(person, needsCanEdit ? AccessLevel["Read/Write"] : undefined)}
          >
            {recordPage.giveAccess}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function CheckAnswer(
  props: Readonly<{
    domain: CheckedRecordDomain;
    check: RecordAccessAnswer;
    person: PermissionGranteeOption;
    noun: string;
    onGiveAccess?: (person: PermissionGranteeOption) => void;
  }>,
) {
  const { check, person, noun } = props;
  const grantorIds = useMemo(
    () => new Set(check.reasons.flatMap((reason) => (reason.grantorUserId && Guid.isValid(reason.grantorUserId) ? [reason.grantorUserId] : []))),
    [check.reasons],
  );
  const grantors = useGetIndividualsByUserIdsQuery(grantorIds.size > 0 ? { userIds: [...grantorIds] } : skipToken);
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const individual of Individual.fromJSONArray(grantors.data, { allowEmpty: true }) ?? []) {
      if (individual.userId && individual.fullName) map.set(individual.userId.value.toLowerCase(), individual.fullName);
    }
    return map;
  }, [grantors.data]);
  const today = new AHDate();
  const Icon = check.canOpen ? CheckCircleOutlineIcon : DoNotDisturbOnOutlinedIcon;

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Icon color={check.canOpen ? "success" : "disabled"} sx={{ mt: 0.25 }} aria-hidden />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {answerText(check, person.label, noun)}
        </Typography>
        {check.canOpen && check.visibleUntil && (
          <Typography variant="body2" color="text.secondary">
            {recordPage.frozen(grantDate(new AHDate(check.visibleUntil), today))}
          </Typography>
        )}
        {check.reasons.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {check.reasons.map((reason) => (
              <Typography component="li" variant="body2" color="text.secondary" key={reason.key}>
                {reasonText(props.domain, reason, noun, (id) => names.get(id.toLowerCase()))}
              </Typography>
            ))}
          </Box>
        )}
        {!check.canOpen && <RefusalRemedies check={check} person={person} noun={noun} onGiveAccess={props.onGiveAccess} />}
      </Box>
    </Box>
  );
}

/** One proposal field that keeps the person out, and the proposal's page when the viewer may change it. */
function ProposalFieldRemedy(
  props: Readonly<{ remedy: RecordAccessRemedy; person: string; proposalName?: string; alone: boolean }>,
) {
  const { remedy } = props;
  // The viewer may not read the field, so the server named neither it nor its proposal. Beside a
  // grant, the answer's first sentence already says a field is needed too.
  if (!remedy.proposalId || !remedy.proposalField) {
    return props.alone ? (
      <Typography variant="body2" color="text.secondary">
        {recordAccessReasons.withheldByProposalField}
      </Typography>
    ) : null;
  }
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {recordAccessRemedies.proposalField(props.person, props.proposalName, fieldName(remedy.proposalField), props.alone)}
      </Typography>
      {remedy.offered && (
        <Button size="small" variant="outlined" sx={{ mt: 1 }} component={RouterLink} to={recordPermissionsPath("proposals", remedy.proposalId)}>
          {recordAccessRemedies.changeProposalAccess}
        </Button>
      )}
    </Box>
  );
}

/** Stands in for the remedy a server from before #6833 does not send: the refusal, naming nothing. */
const UNNAMED_FIELD_REMEDY: readonly RecordAccessRemedy[] = [{ rule: RecordAccessRemedyRule.ProposalField, offered: false }];

/**
 * What would turn a "No" into "Yes", for each refusal the server reports (#6833). A change is offered
 * only where the server says this viewer may make it; a roster place and a draft offer nothing.
 */
function RefusalRemedies(
  props: Readonly<{
    check: RecordAccessAnswer;
    person: PermissionGranteeOption;
    noun: string;
    onGiveAccess?: (person: PermissionGranteeOption) => void;
  }>,
) {
  const { check, person, noun } = props;
  // Only a standing kind is named from the kinds endpoint.
  const kinds = useGetPermissionKindsQuery(
    check.remedies.some((remedy) => remedy.rule === RecordAccessRemedyRule.StandingGrant) ? undefined : skipToken,
  );
  const fieldRemedies = useMemo(
    () => check.remedies.filter((remedy) => remedy.rule === RecordAccessRemedyRule.ProposalField),
    [check.remedies],
  );
  // Stable, so the names are read once per answer.
  const proposalTargets = useMemo(() => {
    const ids = [...new Set(fieldRemedies.flatMap((remedy) => (remedy.proposalId ? [remedy.proposalId] : [])))];
    return ids.length > 0 ? { resourceType: ResourceType.ProposalAccess, ids } : skipToken;
  }, [fieldRemedies]);
  const proposals = useGetPermissionTargetsByIdsQuery(proposalTargets);

  if (check.withheldBy === RecordAccessWithholding.UnsubmittedDraft) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {recordAccessRemedies.unsubmittedDraft}
      </Typography>
    );
  }
  if (check.withheldBy === RecordAccessWithholding.ProposalField) {
    const shown = fieldRemedies.length > 0 ? fieldRemedies : UNNAMED_FIELD_REMEDY;
    return (
      <Box sx={{ display: "grid", gap: 0.5, mt: 0.5 }}>
        {shown.map((remedy, index) => (
          <ProposalFieldRemedy
            key={`${remedy.proposalId ?? "unnamed"}-${remedy.proposalField ?? index}`}
            remedy={remedy}
            person={person.label}
            proposalName={proposals.data?.found.find((target) => target.id === remedy.proposalId)?.name}
            alone
          />
        ))}
      </Box>
    );
  }
  if (check.withheldBy) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {recordPage.withheld}
      </Typography>
    );
  }

  const sentence = nothingReachesText(check.remedies, noun, kinds.data?.kinds);
  const recordGrant = check.remedies.find((remedy) => remedy.rule === RecordAccessRemedyRule.RecordGrant);
  // A server from before #6833 sends no remedies; then the viewer's own grant rights decide, as before.
  const offerGrant = props.onGiveAccess !== undefined && (check.remedies.length === 0 || recordGrant?.offered === true);
  return (
    <>
      {sentence && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sentence}
        </Typography>
      )}
      {offerGrant && (
        <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => props.onGiveAccess?.(person)}>
          {recordPage.giveAccess}
        </Button>
      )}
      {fieldRemedies.map((remedy, index) => (
        <ProposalFieldRemedy
          key={`${remedy.proposalId ?? "unnamed"}-${remedy.proposalField ?? index}`}
          remedy={remedy}
          person={person.label}
          proposalName={proposals.data?.found.find((target) => target.id === remedy.proposalId)?.name}
          alone={false}
        />
      ))}
    </>
  );
}

/** The action choice, offered only on a record type with more than Open. */
function ActionChoice(
  props: Readonly<{ actions: readonly RecordAction[]; value: RecordAction; noun: string; onChange: (action: RecordAction) => void }>,
) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={props.value}
      aria-label={recordActions.choice}
      onChange={(_, value: RecordAction | null) => {
        if (value) props.onChange(value);
      }}
      sx={[
        { "& .MuiToggleButton-root": { minHeight: 40, textTransform: "none", whiteSpace: "nowrap" } },
        touchTargetSx("& .MuiToggleButton-root"),
      ]}
    >
      {props.actions.map((action) => (
        <ToggleButton key={action} value={action}>
          {recordActions.name(action, props.noun)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** The answer area while a question is asked, failed or answered. */
function answerState<T>(
  query: Readonly<{ error?: unknown; isFetching: boolean; data?: T; refetch: () => unknown }>,
  answered: (data: T) => ReactNode,
): ReactNode {
  if (query.error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
        }
      >
        {`${recordPage.checkFailed} ${getErrorMessageFromUnknown(query.error) ?? ""}`.trim()}
      </Alert>
    );
  }
  if (query.isFetching || !query.data) return <Skeleton width="60%" aria-label="Checking access" />;
  return answered(query.data);
}

export default function CheckAccess(
  props: Readonly<{
    domain: CheckedRecordDomain;
    recordId: string;
    noun: string;
    /**
     * Opens Add people with this person chosen, at the level the checked action needs when it needs
     * more than the kind's default. Absent for a viewer who may not grant.
     */
    onGiveAccess?: (person: PermissionGranteeOption, accessLevel?: AccessLevel) => void;
  }>,
) {
  const [person, setPerson] = useState<PermissionGranteeOption | null>(null);
  const [action, setAction] = useState<RecordAction>(RecordAction.Open);
  const actions = recordActionsOf(props.domain);
  const record = person ? { domain: props.domain, recordId: props.recordId, userId: person.value } : undefined;
  const check = useCheckRecordAccessQuery(record && action === RecordAction.Open ? record : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const actionCheck = useCheckRecordActionQuery(record && action !== RecordAction.Open ? { ...record, action } : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const verb = recordActions.verb(action, props.noun);
  const prompt = action === RecordAction.Open ? recordPage.checkAccessPrompt(props.noun) : recordActions.prompt(verb, props.noun);

  let answer: ReactNode = null;
  if (person && action === RecordAction.Open) {
    answer = answerState(check, (data) => (
      <CheckAnswer domain={props.domain} check={data} person={person} noun={props.noun} onGiveAccess={props.onGiveAccess} />
    ));
  } else if (person) {
    answer = answerState(actionCheck, (data) => (
      <ActionAnswer domain={props.domain} check={data} person={person} noun={props.noun} verb={verb} onGiveAccess={props.onGiveAccess} />
    ));
  }

  return (
    <Paper variant="outlined" component="section" aria-label={recordPage.checkAccess} sx={{ p: 2, display: "grid", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
        <Typography variant="subtitle2" component="h2" sx={{ flex: "0 0 auto" }}>
          {recordPage.checkAccess}
        </Typography>
        {actions.length > 1 && <ActionChoice actions={actions} value={action} noun={props.noun} onChange={setAction} />}
        <PersonPicker label={prompt} value={person} onChange={setPerson} />
      </Box>
      <Box role="status" aria-live="polite">
        {answer}
      </Box>
    </Paper>
  );
}
